/**
 * The WhatsApp article newsletter's pure rules (flow-loops-whatsapp,
 * 2026-10-01) — the vocabulary and limits the saga, the three canonical
 * actions and the `/newsletter` page must agree on, in the one package all
 * three can import. Pure consts and functions; its only imports are the tag
 * and edition slug patterns and the newsletter's button titles, from three
 * other pure modules of this package. The page bundles this file through a
 * Vite alias, the same way it bundles `placeholder-email`.
 *
 * Who receives it and how it is sent live in `@layer/saga-runtime`'s
 * `send-whatsapp-newsletter.ts`; this module is only what a form, a JSON
 * Schema and a database CHECK need to say the same thing.
 */

import { NEWSLETTER_EDITION_SLUG_PATTERN } from "./email-newsletter.js";
import {
  NEWSLETTER_OFFER_BUTTON_TITLE,
  NEWSLETTER_OPT_OUT_BUTTON_TITLE,
} from "./newsletter-offer-button.js";
import { TAG_SLUG_PATTERN } from "./schema-primitives.js";

/**
 * The approved MARKETING template an `article` newsletter sends: the article
 * and a booking nudge, opening with who the studio is and closing with a
 * "Not interested" button (newsletter-v2-not-interested, 2026-10-31). Meta id
 * 3032408280429762.
 */
export const WHATSAPP_NEWSLETTER_TEMPLATE_NAME = "member_article_and_booking_v2";

/**
 * The MARKETING template an `article_offer` newsletter sends (2026-10-29,
 * Mario: "another option … more promotional"): the same opening, article line
 * and buttons, with the closing line an offer — "This week only: {{4}}.
 * Reply here or tap below to book your next session." One template for every
 * offer, so a new offer needs no new approval at Meta. Meta id
 * 1102285205968231 (v2, 2026-10-31).
 */
export const WHATSAPP_NEWSLETTER_OFFER_TEMPLATE_NAME = "member_article_offer_v2";

/**
 * The MARKETING template an `offer` newsletter sends (2026-10-29, Mario: "a
 * third option, which is purely the offer without the newsletter article"):
 * "Hi {{1}}, it's {{2}}, your EMS studio. This week only: {{3}}. Tap Tell me
 * more for the details." with two quick replies — "Tell me more", whose tap
 * starts a conversation about the offer, and "Not interested" — and no
 * article, no link (`newsletter-offer-button.ts`). Meta id 1324363543070013
 * (v2, 2026-10-31).
 */
export const WHATSAPP_NEWSLETTER_OFFER_ONLY_TEMPLATE_NAME = "member_offer_v2";

/**
 * Clavis's own words for an `article` newsletter (newsletter-studio-words,
 * 2026-11-01 — Mario: "Clavis's own words"). Clavis writes to studio owners,
 * not to an EMS studio's members, so it says neither "your EMS studio" nor
 * "book your next session": its link opens any useclavis.ai page by its path,
 * and its second button books a call with Mario. Submitted to the Clavis
 * WhatsApp account (…9810) on 2026-11-01, Meta id 1096352046620902.
 */
export const WHATSAPP_NEWSLETTER_CLAVIS_TEMPLATE_NAME = "clavis_update_v1";

/**
 * The three templates the newsletter sent until 2026-10-31, when Meta flagged
 * the studio's account for "Sending spam" after an offer some recipients did
 * not recognise (Mario chose new names over editing these in place: a new
 * template starts with no quality history, and the switch is one deploy).
 * Nothing sends them any more; they stay approved at Meta, and here, only so
 * the chat agent's history still shows a send made with one in the words it
 * went out in — its stored row reads `[template: <name>]`.
 */
export const WHATSAPP_NEWSLETTER_RETIRED_TEMPLATE_NAME = "member_article_and_booking";
export const WHATSAPP_NEWSLETTER_RETIRED_OFFER_TEMPLATE_NAME = "member_article_offer";
export const WHATSAPP_NEWSLETTER_RETIRED_OFFER_ONLY_TEMPLATE_NAME = "member_offer";

/**
 * What a newsletter says: `article` (the article and a booking nudge),
 * `article_offer` (the article and an offer) or `offer` (the offer on its
 * own, with a "Tell me more" button; the page's "Offer" with "Include the
 * article" unticked). The input's `message` may be left out, and then it is
 * `article` — every send before 2026-10-29.
 */
export const WHATSAPP_NEWSLETTER_MESSAGES = ["article", "article_offer", "offer"] as const;

export type WhatsAppNewsletterMessage = (typeof WHATSAPP_NEWSLETTER_MESSAGES)[number];

/** Does this message link an article (and so carry the line about it)? */
export function whatsAppNewsletterMessageHasArticle(message: WhatsAppNewsletterMessage): boolean {
  return message !== "offer";
}

/** Does this message carry an offer line? */
export function whatsAppNewsletterMessageHasOffer(message: WhatsAppNewsletterMessage): boolean {
  return message !== "article";
}

/**
 * What a newsletter template's variable carries. `studio_name` (2026-10-31)
 * is the studio's own name, `studios.name` — "Hi {{1}}, it's {{2}}, your EMS
 * studio." — so one template's words serve every studio.
 */
export type WhatsAppNewsletterVariable = "first_name" | "studio_name" | "blurb" | "offer_line";

/** How each variable reads in a sentence — the send's refusals and the page name them so. */
export const WHATSAPP_NEWSLETTER_VARIABLE_PHRASES: Readonly<
  Record<WhatsAppNewsletterVariable, string>
> = {
  first_name: "the member's first name",
  studio_name: "the studio's name",
  blurb: "the line about the article",
  offer_line: "the offer",
};

/** One newsletter template's words, as Meta approved them. */
export interface WhatsAppNewsletterMessageCopy {
  /** The template's name at Meta — what a send names and its stored row reads. */
  readonly name: string;
  /** The message the template says. */
  readonly message: WhatsAppNewsletterMessage;
  /** Meta's approved body, verbatim; `{{n}}` is the n-th of {@link variables}. */
  readonly body: string;
  /** What each `{{n}}` carries, in Meta's order — the order the send fills them. */
  readonly variables: ReadonlyArray<WhatsAppNewsletterVariable>;
  /** Meta's approved footer, verbatim; `null` for a template with none. */
  readonly footer: string | null;
  /** The buttons' labels, in Meta's order. */
  readonly buttons: ReadonlyArray<string>;
}

/** The v2 templates' footer: what the "Not interested" button does. */
export const WHATSAPP_NEWSLETTER_OPT_OUT_FOOTER =
  "Not interested? Tap below and we'll stop these messages";

const ARTICLE_COPY: WhatsAppNewsletterMessageCopy = {
  name: WHATSAPP_NEWSLETTER_TEMPLATE_NAME,
  message: "article",
  body: "Hi {{1}}, it's {{2}}, your EMS studio. We've put together something we think you'll find useful — {{3}}.\n\nHave a read whenever suits, and book your next session if you'd like one.",
  variables: ["first_name", "studio_name", "blurb"],
  footer: WHATSAPP_NEWSLETTER_OPT_OUT_FOOTER,
  buttons: ["Read the article", "Book a session", NEWSLETTER_OPT_OUT_BUTTON_TITLE],
};

const ARTICLE_OFFER_COPY: WhatsAppNewsletterMessageCopy = {
  name: WHATSAPP_NEWSLETTER_OFFER_TEMPLATE_NAME,
  message: "article_offer",
  body: "Hi {{1}}, it's {{2}}, your EMS studio. We've put together something we think you'll find useful — {{3}}.\n\nThis week only: {{4}}. Reply here or tap below to book your next session.",
  variables: ["first_name", "studio_name", "blurb", "offer_line"],
  footer: WHATSAPP_NEWSLETTER_OPT_OUT_FOOTER,
  buttons: ["Read the article", "Book a session", NEWSLETTER_OPT_OUT_BUTTON_TITLE],
};

const OFFER_COPY: WhatsAppNewsletterMessageCopy = {
  name: WHATSAPP_NEWSLETTER_OFFER_ONLY_TEMPLATE_NAME,
  message: "offer",
  body: "Hi {{1}}, it's {{2}}, your EMS studio.\n\nThis week only: {{3}}. Tap Tell me more for the details.",
  variables: ["first_name", "studio_name", "offer_line"],
  footer: WHATSAPP_NEWSLETTER_OPT_OUT_FOOTER,
  buttons: [NEWSLETTER_OFFER_BUTTON_TITLE, NEWSLETTER_OPT_OUT_BUTTON_TITLE],
};

const CLAVIS_ARTICLE_COPY: WhatsAppNewsletterMessageCopy = {
  name: WHATSAPP_NEWSLETTER_CLAVIS_TEMPLATE_NAME,
  message: "article",
  body: "Hi {{1}}, it's {{2}}. We've put together something we think you'll find useful — {{3}}.\n\nHave a look whenever suits, and book a call with Mario if you'd like to see it running in your studio.",
  variables: ["first_name", "studio_name", "blurb"],
  footer: WHATSAPP_NEWSLETTER_OPT_OUT_FOOTER,
  buttons: ["Have a look", "Book a call", NEWSLETTER_OPT_OUT_BUTTON_TITLE],
};

/**
 * The templates each message may be sent with, and their words: the shared
 * template first — an EMS studio's, to its members — then any studio's own
 * (newsletter-studio-words, 2026-11-01). A studio's catalog lists exactly one
 * of a message's templates, and that is the one its newsletter sends
 * (`resolveWhatsAppNewsletterTemplate` in `@layer/saga-runtime`), so the words
 * a message goes out in are the studio's choice, made at Meta and in its YAML.
 *
 * The words are the approved body, footer and buttons, verbatim — the shared
 * three submitted on Mario's go and read back from the WABA's
 * `message_templates` on 2026-10-31, all APPROVED MARKETING, `en`, and
 * byte-identical to what was submitted; Clavis's submitted the same way on
 * 2026-11-01. The repo's one copy of these words (offer-in-history,
 * 2026-10-30): the `/newsletter` page previews a send in its studio's
 * template's words, the send fills each template's variables in its order,
 * and the chat agent's history shows a send in them (`@layer/chat-translator`
 * → `newsletterHistoryBody`). Meta renders the real message, so a template
 * changed at Meta is changed here in the same change.
 */
export const WHATSAPP_NEWSLETTER_MESSAGE_TEMPLATES: Readonly<
  Record<
    WhatsAppNewsletterMessage,
    readonly [WhatsAppNewsletterMessageCopy, ...WhatsAppNewsletterMessageCopy[]]
  >
> = {
  article: [ARTICLE_COPY, CLAVIS_ARTICLE_COPY],
  article_offer: [ARTICLE_OFFER_COPY],
  offer: [OFFER_COPY],
};

/**
 * The three templates retired on 2026-10-31, in the words Meta approved them
 * in (as read off the WABA on 2026-10-30).
 */
const RETIRED_COPIES: ReadonlyArray<WhatsAppNewsletterMessageCopy> = [
  {
    name: WHATSAPP_NEWSLETTER_RETIRED_TEMPLATE_NAME,
    message: "article",
    body: "Hi {{1}}, we've put together something we think you'll find useful — {{2}}.\n\nHave a read whenever suits, and book your next session if you'd like one.",
    variables: ["first_name", "blurb"],
    footer: null,
    buttons: ["Read the article", "Book a session"],
  },
  {
    name: WHATSAPP_NEWSLETTER_RETIRED_OFFER_TEMPLATE_NAME,
    message: "article_offer",
    body: "Hi {{1}}, we've put together something we think you'll find useful — {{2}}.\n\nThis week only: {{3}}. Reply here or tap below to book your next session.",
    variables: ["first_name", "blurb", "offer_line"],
    footer: null,
    buttons: ["Read the article", "Book a session"],
  },
  {
    name: WHATSAPP_NEWSLETTER_RETIRED_OFFER_ONLY_TEMPLATE_NAME,
    message: "offer",
    body: "Hi {{1}}, we've got something for you this week.\n\nThis week only: {{2}}. Tap below and we'll tell you more.",
    variables: ["first_name", "offer_line"],
    footer: null,
    buttons: [NEWSLETTER_OFFER_BUTTON_TITLE],
  },
];

/**
 * Every newsletter template by name — every message's current ones and the
 * three retired on 2026-10-31. A send's stored row names its template, so
 * this is how the history, the send itself and the page know what a row, a
 * run or a studio's message says.
 */
export const WHATSAPP_NEWSLETTER_TEMPLATE_COPY: ReadonlyMap<string, WhatsAppNewsletterMessageCopy> =
  new Map(
    [...Object.values(WHATSAPP_NEWSLETTER_MESSAGE_TEMPLATES).flat(), ...RETIRED_COPIES].map(
      (copy) => [copy.name, copy] as const,
    ),
  );

/** One piece of a body, in reading order: its words, or one of its variables. */
export type WhatsAppNewsletterBodyPart =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "variable"; readonly variable: WhatsAppNewsletterVariable };

/** A template's body cut into its words and its variables, in reading order. */
export function whatsAppNewsletterCopyBodyParts(
  copy: WhatsAppNewsletterMessageCopy,
): ReadonlyArray<WhatsAppNewsletterBodyPart> {
  const { body, variables } = copy;
  const parts: WhatsAppNewsletterBodyPart[] = [];
  let from = 0;
  for (const match of body.matchAll(/\{\{(\d+)\}\}/g)) {
    const variable = variables[Number(match[1]) - 1];
    if (variable === undefined) {
      throw new Error(`whatsapp_newsletter_copy_invalid: ${copy.name} names {{${String(match[1])}}}`);
    }
    if (match.index > from) parts.push({ kind: "text", text: body.slice(from, match.index) });
    parts.push({ kind: "variable", variable });
    from = match.index + match[0].length;
  }
  if (from < body.length) parts.push({ kind: "text", text: body.slice(from) });
  return parts;
}

/**
 * The offer line finishes "This week only: …" in both offer templates — short
 * enough to read on a phone, and the body around it is fixed.
 */
export const WHATSAPP_NEWSLETTER_OFFER_LINE_MAX_CHARS = 150;

/**
 * The JSON-Schema rule pairing `offer_line` with the two offer messages:
 * required for `article_offer` and `offer`, refused otherwise (a left-out
 * `message` is `article`). Migration 262's CHECK says the same to the
 * database.
 */
export const WHATSAPP_NEWSLETTER_OFFER_LINE_RULE = {
  if: { properties: { message: { enum: ["article_offer", "offer"] } }, required: ["message"] },
  then: { required: ["offer_line"] },
  else: { not: { required: ["offer_line"] } },
} as const;

/**
 * The send's rule pairing the article with the two article messages: an
 * `offer` refuses `article_slug` and `blurb`, anything else requires both.
 * Migration 262's CHECK says the same to the database.
 */
export const WHATSAPP_NEWSLETTER_ARTICLE_RULE = {
  if: { properties: { message: { const: "offer" } }, required: ["message"] },
  then: { allOf: [{ not: { required: ["article_slug"] } }, { not: { required: ["blurb"] } }] },
  else: { required: ["article_slug", "blurb"] },
} as const;

/** The preview's version — it takes the article but never the line about it. */
export const WHATSAPP_NEWSLETTER_PREVIEW_ARTICLE_RULE = {
  if: { properties: { message: { const: "offer" } }, required: ["message"] },
  then: { not: { required: ["article_slug"] } },
  else: { required: ["article_slug"] },
} as const;

/**
 * The discount code an offer names — `list_stripe_coupons`' `coupon_id`:
 * letters, digits, hyphen and underscore, leading alphanumeric. That is a
 * Stripe-minted id and every custom code `create_stripe_coupon` accepts.
 */
export const WHATSAPP_NEWSLETTER_OFFER_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
export const WHATSAPP_NEWSLETTER_OFFER_CODE_MAX_CHARS = 64;

/**
 * The rules pairing `offer_code` with the messages. An `offer` must name its
 * code: it leaves out anyone already sent that code, and the chat agent sells
 * with it when a member taps "Tell me more". An `article_offer` records the
 * code staff picked, if they picked one, so a later offer-only send can leave
 * its recipients out. An `article` refuses one. Migration 262's CHECK says the
 * same to the database.
 */
export const WHATSAPP_NEWSLETTER_OFFER_CODE_RULES = [
  {
    if: { properties: { message: { const: "offer" } }, required: ["message"] },
    then: { required: ["offer_code"] },
  },
  {
    if: { properties: { message: { enum: ["article_offer", "offer"] } }, required: ["message"] },
    else: { not: { required: ["offer_code"] } },
  },
] as const;

/** `offer_code` as a JSON-Schema property, shared by the preview and the send. */
export const WHATSAPP_NEWSLETTER_OFFER_CODE_PROPERTY = {
  type: "string",
  minLength: 1,
  maxLength: WHATSAPP_NEWSLETTER_OFFER_CODE_MAX_CHARS,
  pattern: WHATSAPP_NEWSLETTER_OFFER_CODE_PATTERN.source,
} as const;

/**
 * Blast-radius cap. Not a Meta limit — a guard: an audience query that lost
 * its WHERE clause is refused rather than messaged, and unlike an email a
 * WhatsApp message cannot be unsent. Meta's own limit is a separate check,
 * {@link decideWhatsAppNewsletterMetaGuard}.
 */
export const WHATSAPP_NEWSLETTER_MAX_RECIPIENTS = 2_000;

/** The line about the article finishes a sentence in the template body. */
export const WHATSAPP_NEWSLETTER_BLURB_MAX_CHARS = 200;

/**
 * ONE path segment. Meta appends the slug to the approved button URL, so a
 * value carrying `/`, `?`, `#` or a scheme could point the button somewhere
 * other than the article.
 */
export const WHATSAPP_NEWSLETTER_SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
export const WHATSAPP_NEWSLETTER_SLUG_MAX_CHARS = 100;

/**
 * Who a newsletter goes to. `tag` (2026-10-02) is the members carrying ANY of
 * the tags named by their slugs in the actions' `audience_tag_slugs` — the
 * studio's own way of naming a group, picked on the page from all of its
 * tags (one tag until 2026-10-29). `newsletter` (2026-10-29) is the members
 * an email newsletter sent through Clavis went to — ANY of the editions named
 * by their slugs in `audience_newsletter_slugs`. Either way one member is one
 * fact row, so someone in two picked tags or sent two picked newsletters is
 * still counted, and messaged, once.
 */
export const WHATSAPP_NEWSLETTER_AUDIENCES = [
  "everyone",
  "customers",
  "leads",
  "tag",
  "newsletter",
] as const;

export type WhatsAppNewsletterAudience =
  (typeof WHATSAPP_NEWSLETTER_AUDIENCES)[number];

/** How many tags one `tag` audience may name. */
export const WHATSAPP_NEWSLETTER_AUDIENCE_TAGS_MAX = 20;

/** How many email newsletters one `newsletter` audience may name. */
export const WHATSAPP_NEWSLETTER_AUDIENCE_NEWSLETTERS_MAX = 20;

/**
 * The two audience lists as JSON-Schema properties, shared by the preview's
 * and the send's input schemas (which pair each with its audience through
 * `WHATSAPP_NEWSLETTER_AUDIENCE_PAIRING_RULES`): a tag is `add_member_tag`'s
 * slug, a newsletter an edition's slug, each list distinct and capped. Here,
 * in the leaf, for the pairing rules' reason — the action schemas spread it
 * at module scope. Only keywords MCP clients accept
 * (`apps/mcp-server/tests/tool-schema-keywords.test.ts`).
 */
export const WHATSAPP_NEWSLETTER_AUDIENCE_LIST_PROPERTIES = {
  audience_tag_slugs: {
    type: "array",
    minItems: 1,
    maxItems: WHATSAPP_NEWSLETTER_AUDIENCE_TAGS_MAX,
    uniqueItems: true,
    items: { type: "string", pattern: TAG_SLUG_PATTERN },
  },
  audience_newsletter_slugs: {
    type: "array",
    minItems: 1,
    maxItems: WHATSAPP_NEWSLETTER_AUDIENCE_NEWSLETTERS_MAX,
    uniqueItems: true,
    items: { type: "string", pattern: NEWSLETTER_EDITION_SLUG_PATTERN },
  },
} as const;

/**
 * The JSON-Schema rules pairing each audience with its list:
 * `audience_tag_slugs` is required for `tag` and refused otherwise, and
 * `audience_newsletter_slugs` likewise for `newsletter`. Both newsletter
 * actions spread them into `allOf`, so the chokepoint is the one place this
 * is enforced; migration 260's pairing CHECK says the same to the database.
 * Plain data here because the action schemas read it at module scope, which
 * only a leaf may offer — `@layer/canonical-actions` and
 * `@layer/saga-runtime` import each other, and a module-scope read through
 * the cycle can hit the temporal dead zone
 * (`tests/package-barrel-esm-init.test.ts`).
 */
export const WHATSAPP_NEWSLETTER_AUDIENCE_PAIRING_RULES = [
  {
    if: { properties: { audience: { const: "tag" } }, required: ["audience"] },
    then: { required: ["audience_tag_slugs"] },
    else: { not: { required: ["audience_tag_slugs"] } },
  },
  {
    if: { properties: { audience: { const: "newsletter" } }, required: ["audience"] },
    then: { required: ["audience_newsletter_slugs"] },
    else: { not: { required: ["audience_newsletter_slugs"] } },
  },
] as const;

/**
 * Why an audience member is not sent the article, in the order the rule
 * checks — and the order the page lists them in.
 * `do_not_contact` (2026-10-31): the member carries the do-not-contact tag
 * (`MARKETING_DO_NOT_CONTACT_TAG_SLUG`), which wins over everything but "not
 * active", as it does for the email newsletter (Mario: the WhatsApp
 * newsletter skips anyone tagged do-not-contact, just as the email one does).
 * `unsubscribed_from_newsletter` (2026-10-29): a `newsletter` audience leaves
 * out anyone who used the unsubscribe link in one of the picked emails, even
 * when they left only the email list (Mario: someone who said no to an offer
 * by email is not sent it again on WhatsApp). `number_paused`
 * (deliverability-ladder, 2026-10-31): the number the member would be sent
 * it on is on the growing pause after Meta reported it not on WhatsApp — the
 * rule every direct WhatsApp send asks (`whatsAppRefusal`) — so the send
 * leaves it out up front instead of being refused at Meta (the approved
 * plan: the preview leaves paused numbers out). `us_number`
 * (deliverability-ladder D7, 2026-10-31): that number is a US one and the
 * studio leaves US numbers out (`studio.whatsapp_marketing_skip_us_numbers`),
 * because Meta does not deliver marketing templates to them and refuses each
 * with 131049 (Mario's D7(a): left out before sending, and shown in the
 * preview). `already_received`: another
 * send already reached this member with the same article — or, for an
 * `offer` (2026-10-29), with the same discount code, by an offer or an
 * article + offer send. `duplicate_number`: another member of the same send
 * resolves to the same WhatsApp number (a couple, a parent booking for a
 * child, an old duplicate record), so that phone gets the message once.
 */
export const WHATSAPP_NEWSLETTER_SKIP_REASONS = [
  "member_inactive",
  "do_not_contact",
  "marketing_opted_out",
  "unsubscribed_from_newsletter",
  "whatsapp_opted_out",
  "no_whatsapp_number",
  "number_paused",
  "us_number",
  "already_received",
  "duplicate_number",
] as const;

export type WhatsAppNewsletterSkipReason =
  (typeof WHATSAPP_NEWSLETTER_SKIP_REASONS)[number];

/**
 * The line about the article as it is sent: one line (Meta refuses a
 * template parameter with a newline, a tab or a run of spaces), without a
 * closing full stop — the template's body puts one after it, so a line
 * typed as a sentence would otherwise arrive ending "..". The offer line
 * (also followed by a full stop, in both offer templates) is sent through the
 * same rule.
 */
export function normalizeWhatsAppNewsletterBlurb(raw: string): string {
  return raw
    .replace(/\s+/gu, " ")
    .trim()
    .replace(/\.+$/u, "")
    .trimEnd();
}

/**
 * The slug from what staff typed: a slug as-is, or — pasted from the
 * browser — the last path segment of an article URL. Not validated here;
 * the caller checks the result against {@link WHATSAPP_NEWSLETTER_SLUG_PATTERN}.
 */
export function whatsAppNewsletterSlugFromInput(raw: string): string {
  const typed = raw.trim();
  if (!/^https?:\/\//iu.test(typed)) return typed;
  try {
    const segments = new URL(typed).pathname.split("/").filter((s) => s.length > 0);
    const last = segments[segments.length - 1] ?? "";
    return decodeURIComponent(last);
  } catch {
    return typed;
  }
}

/** A pasted link that is not the page members would open — see below. */
export interface WhatsAppNewsletterPastedLinkMismatch {
  /** What staff pasted, trimmed. */
  readonly pastedUrl: string;
  /** Its host, e.g. `beyondtheline.co.uk`. */
  readonly pastedHost: string;
  /** The blog's own site (ignoring `www.`), but another page on it. */
  readonly sameSite: boolean;
}

/**
 * Does a pasted article LINK point somewhere other than the page members
 * would open? The template's "Read the article" button can only open the
 * address Meta approved with the slug appended (e.g.
 * `https://e-pulsive.co.uk/blog/<slug>`), and the page sends the pasted
 * link's last segment as that slug — so a press article on another site, or
 * another page of the blog, would silently become a lookup of a blog post of
 * the same slug (2026-10-02: a beyondtheline.co.uk link was checked as
 * `e-pulsive.co.uk/blog/e-pulsive-ems`).
 *
 * `null` when the input is a slug rather than a link, when either does not
 * parse, or when both name the same page: the host ignoring `www.`, the path
 * ignoring a trailing slash; the scheme, query and fragment are ignored.
 */
export function whatsAppNewsletterPastedLinkMismatch(
  input: string,
  articleUrl: string,
): WhatsAppNewsletterPastedLinkMismatch | null {
  const typed = input.trim();
  if (!/^https?:\/\//iu.test(typed)) return null;
  let pasted: URL;
  let opened: URL;
  try {
    pasted = new URL(typed);
    opened = new URL(articleUrl);
  } catch {
    return null;
  }
  const site = (u: URL): string => u.hostname.toLowerCase().replace(/^www\./u, "");
  const page = (u: URL): string => {
    const path = u.pathname.replace(/\/+$/u, "");
    try {
      return decodeURIComponent(path);
    } catch {
      return path;
    }
  };
  const sameSite = site(pasted) === site(opened);
  if (sameSite && page(pasted) === page(opened)) return null;
  return { pastedUrl: typed, pastedHost: pasted.hostname, sameSite };
}

// ---------------------------------------------------------------------------
// Meta's messaging limit — checked before a send (newsletter-meta-limit-guard,
// 2026-10-02).
// ---------------------------------------------------------------------------

/**
 * Meta's messaging limit: how many DIFFERENT people a business may message
 * outside an open customer-service window in a moving 24 hours. It is set per
 * business portfolio and shared by every number in it, and Meta reports it as
 * the WABA's `whatsapp_business_manager_messaging_limit`. `null` = unlimited.
 * `TIER_50` and `TIER_1K` are older values Meta may still report.
 *
 * Once the day's limit is used, Meta refuses messages to anyone new — the
 * cart reminders, booking messages and staff alerts too — so a newsletter
 * that would use it up is refused before it is queued.
 */
export const WHATSAPP_MESSAGING_LIMIT_TIERS = {
  TIER_50: 50,
  TIER_250: 250,
  TIER_1K: 1_000,
  TIER_2K: 2_000,
  TIER_10K: 10_000,
  TIER_100K: 100_000,
  TIER_UNLIMITED: null,
} as const satisfies Readonly<Record<string, number | null>>;

export type WhatsAppMessagingLimitTier = keyof typeof WHATSAPP_MESSAGING_LIMIT_TIERS;

/** A tier this module knows the size of; anything else is unreadable. */
export function isWhatsAppMessagingLimitTier(
  raw: string,
): raw is WhatsAppMessagingLimitTier {
  return Object.hasOwn(WHATSAPP_MESSAGING_LIMIT_TIERS, raw);
}

/** The number's quality rating as Meta reports it; anything else is `UNKNOWN`. */
export type WhatsAppMetaQualityRating = "GREEN" | "YELLOW" | "RED" | "UNKNOWN";

/** Meta's `health_status.can_send_message`. */
export type WhatsAppMetaCanSendMessage = "AVAILABLE" | "LIMITED" | "BLOCKED";

/**
 * One reading of the studio's WhatsApp account, taken just before a check or
 * a send. `unreadable` carries why — the guard then refuses, because guessing
 * wrong cannot be undone.
 */
export type WhatsAppMetaMessagingReading =
  | {
      readonly status: "ok";
      /** Its size is {@link WHATSAPP_MESSAGING_LIMIT_TIERS}`[tier]`. */
      readonly tier: WhatsAppMessagingLimitTier;
      /** The sending number's rating; `null` when Meta did not say. */
      readonly qualityRating: WhatsAppMetaQualityRating | null;
      /** The worse of the number's and the account's; `null` when Meta did not say. */
      readonly canSendMessage: WhatsAppMetaCanSendMessage | null;
    }
  | { readonly status: "unreadable"; readonly reason: string };

/**
 * The share of the limit a newsletter leaves for everything else the studio
 * sends that day — cart reminders, booking messages, flows, staff alerts —
 * and for the few template sends the count below cannot see (urgent staff
 * alerts are logged with their text, staff sign-in codes not at all).
 */
export const WHATSAPP_NEWSLETTER_META_RESERVE_FRACTION = 0.1;

export function whatsAppNewsletterMetaReserve(dailyLimit: number): number {
  return Math.ceil(dailyLimit * WHATSAPP_NEWSLETTER_META_RESERVE_FRACTION);
}

/** What stops a send, in the order it is checked. */
export type WhatsAppNewsletterMetaVerdict =
  | "ok"
  | "send_in_flight"
  | "unreadable"
  | "number_blocked"
  | "quality_red"
  | "over_limit";

/** What is worth knowing but does not stop a send. */
export type WhatsAppNewsletterMetaWarning =
  | "quality_yellow"
  | "quality_unknown"
  | "number_limited";

export interface WhatsAppNewsletterMetaDecision {
  readonly verdict: WhatsAppNewsletterMetaVerdict;
  /** `null` when the limit is unlimited or could not be read. */
  readonly dailyLimit: number | null;
  readonly reserve: number | null;
  /**
   * How many more different people a newsletter may reach now: the limit,
   * less the reserve, less everyone sent a template in the last 24 hours.
   * Never negative; `null` when there is no limit or it could not be read.
   */
  readonly roomToday: number | null;
  readonly warnings: ReadonlyArray<WhatsAppNewsletterMetaWarning>;
}

/**
 * Whether a newsletter to `recipientCount` people may go now — the ONE rule
 * the page's Check shows and the send enforces.
 *
 * Counted conservatively: someone sent a template earlier today who is also
 * in this audience counts twice, although Meta counts them once.
 *
 *   - `send_in_flight` — another newsletter is still sending: its recipients
 *     are not all in the log yet, so the room cannot be known (and two sends
 *     of one article could otherwise reach the same people twice);
 *   - `unreadable` — Meta's limit could not be read: refuse, never guess;
 *   - `number_blocked` — Meta says the number cannot send at all;
 *   - `quality_red` — the number is rated low; a mass send risks it being
 *     restricted, and it also carries the chat agent and the cart reminders;
 *   - `over_limit` — more people than the room left today.
 */
export function decideWhatsAppNewsletterMetaGuard(args: {
  readonly reading: WhatsAppMetaMessagingReading;
  readonly messagedLast24h: number;
  readonly sendInFlight: boolean;
  readonly recipientCount: number;
}): WhatsAppNewsletterMetaDecision {
  const { reading } = args;
  if (reading.status === "unreadable") {
    return {
      verdict: args.sendInFlight ? "send_in_flight" : "unreadable",
      dailyLimit: null,
      reserve: null,
      roomToday: null,
      warnings: [],
    };
  }

  const warnings: WhatsAppNewsletterMetaWarning[] = [];
  if (reading.qualityRating === "YELLOW") warnings.push("quality_yellow");
  if (reading.qualityRating === null || reading.qualityRating === "UNKNOWN") {
    warnings.push("quality_unknown");
  }
  if (reading.canSendMessage === "LIMITED") warnings.push("number_limited");

  const dailyLimit: number | null = WHATSAPP_MESSAGING_LIMIT_TIERS[reading.tier];
  const reserve = dailyLimit === null ? null : whatsAppNewsletterMetaReserve(dailyLimit);
  const roomToday =
    dailyLimit === null || reserve === null
      ? null
      : Math.max(0, dailyLimit - reserve - args.messagedLast24h);

  let verdict: WhatsAppNewsletterMetaVerdict = "ok";
  if (args.sendInFlight) verdict = "send_in_flight";
  else if (reading.canSendMessage === "BLOCKED") verdict = "number_blocked";
  else if (reading.qualityRating === "RED") verdict = "quality_red";
  else if (roomToday !== null && args.recipientCount > roomToday) verdict = "over_limit";

  return { verdict, dailyLimit, reserve, roomToday, warnings };
}
